import { Injectable } from '@angular/core';
import {
  AppConfigService,
  DashboardResponse,
  IDP_APPS
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { Observable, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { SessionEndpointKeys } from '../../models/session.interfaces';

/**
 * Helper service for session management.
 * Provides login status observable and redirect functionality.
 */
@Injectable()
export class SessionHelperService {
  /** Observable that emits the current login status */
  loggedIn$: Observable<boolean>;

  /** Whether to redirect to login page when not authenticated */
  protected _redirect = true;

  /** ReplaySubject for login status - allows late subscribers to get the last value */
  private _loggedIn$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  /** ReplaySubject for dashboard data */
  protected _dashboardData$: ReplaySubject<Partial<DashboardResponse>> =
    new ReplaySubject<Partial<DashboardResponse>>(1);

  constructor(protected appConfig: AppConfigService) {
    this.loggedIn$ = this._loggedIn$.asObservable();
  }

  /**
   * Validates redirect based on login status.
   * If not logged in and redirect is enabled, redirects to login page.
   */
  protected validateRedirect(status: boolean, loginRedirect = false): void {
    if ((!status && this._redirect) || loginRedirect) {
      this.redirect();
    }
  }

  /**
   * Sets the response values for login status and dashboard data.
   */
  protected setResponseValues(
    response: Partial<DashboardResponse>,
    status: boolean
  ): void {
    this._loggedIn$.next(status);
    this._dashboardData$.next(response);
  }

  /**
   * Redirects to the login page with the current URL as refreshURL.
   */
  protected redirect(): void {
    this.appConfig
      .getConfigValue(IDP_APPS.COMMON, SessionEndpointKeys.LOGIN_URL)
      .pipe(take(1))
      .subscribe((loginUrl) => {
        const pathname = window.location.pathname;
        const search = window.location.search;
        const fullPath = pathname + (search ? search : '');

        const url = `${
          window.location.origin
        }${loginUrl}?refreshURL=${encodeURIComponent(fullPath)}`;
        window.open(url, '_self');
      });
  }
}
